/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Time 필드 클래스를 정의한다.
 *
 * @file /src/components/form/field/time/Time.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 7. 2.
 */
import * as parent from '@components/form/field/Field';
import { Select } from '@components/form/field/select/Select';
import { Local } from '@models/store/list/Local';
import './Time.css';

type Step = 10 | 30 | 60;

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 증감값 (분 단위, 기본값: 60)
     */
    step?: Step;

    /**
     * 필드 최소값 ('HH:mm' 형식, 기본값: '09:00')
     */
    minValue?: string;

    /**
     * 필드 최댓값 ('HH:mm' 형식, 기본값: '18:00')
     */
    maxValue?: string;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Time extends parent.Field {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'time';

    /**
     * 증감값 (분 단위)
     */
    step: Step;

    /**
     * 필드 최소값 ('HH:mm' 형식)
     */
    minValue: string;

    /**
     * 필드 최댓값 ('HH:mm' 형식)
     */
    maxValue: string;

    /**
     * 시간 선택 Select 필드
     */
    select: Select = null;

    /**
     * Time 필드 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<Time> = null) {
        super(properties);

        this.step = this.properties?.step ?? 60;
        this.minValue = this.properties?.minValue ?? '09:00';
        this.maxValue = this.properties?.maxValue ?? '18:00';
    }

    /**
     * 시간 선택 Select 필드를 가져온다.
     *
     * @returns 시간 선택 Select 필드
     */
    getSelect(): Select {
        if (this.select === null) {
            this.select = new Select({
                fieldWidth: 120,
                search: false,
                autoValue: true,
                initOnChange: false,
                store: new Local({
                    fields: [{ name: 'value', type: 'string' }],
                    provider: {
                        dataFields: ['value', 'display'],
                        loader: async () => {
                            const records = [];
                            const min = this.#timeToMinutes(this.minValue);
                            const max = this.#timeToMinutes(this.maxValue);

                            for (let t = min; t <= max; t += this.step) {
                                const timeStr = this.#minutesToTime(t);
                                records.push({ value: timeStr, display: timeStr });
                            }

                            return records;
                        },
                    },
                }),
                listeners: {
                    change: async () => {
                        await this.#onSelectChange();
                    },
                },
            });
        }

        return this.select;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 'HH:mm' 형식 시간 문자열
     * @param is_init - 초기값여부
     */
    async setValue(value: string, is_init: boolean = false): Promise<void> {
        await this.lockValue();

        this.value = value;
        await this.getSelect().setValue(value, is_init);

        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 'HH:mm' 형식 시간 문자열
     */
    async getValue(): Promise<string> {
        return (await this.getSelect().getValue()) ?? null;
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.getSelect().setDisabled(disabled);

        return super.setDisabled(disabled);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().append(this.getSelect().$getComponent());
        await this.getSelect().render();
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        this.select?.remove(false);
        await super.remove(removeDom);
    }

    /**
     * 'HH:mm' 문자열을 분으로 변환한다.
     *
     * @param time - 'HH:mm' 형식 시간 문자열
     * @returns 분 단위 숫자
     */
    #timeToMinutes(time: string): number {
        const [h, m] = time.split(':').map(Number);
        return h * 60 + m;
    }

    /**
     * 분을 'HH:mm' 문자열로 변환한다.
     *
     * @param minutes - 분 단위 숫자
     * @returns 'HH:mm' 형식 시간 문자열
     */
    #minutesToTime(minutes: number): string {
        const h = Math.floor(minutes / 60);
        const m = minutes % 60;
        return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
    }

    /**
     * Select 값 변경 시 Time 필드값을 업데이트한다.
     */
    async #onSelectChange(): Promise<void> {
        if (this.isLoading() == false) {
            const value = await this.getSelect().getValue();
            await this.setValue(value as string, false);
        }
    }
}
